<template>
  <v-app :style="styles">
      <DisplayWatcher />

    <!-- Covers the safe-area strip on notched devices, and on iOS Safari also
         fixes the colour the browser samples for its status bar. See
         resolveSystemBarHeight -- the height is 0 where neither applies. -->
    <v-system-bar
      v-if="systemBarProps.height"
      v-bind="systemBarProps"
      fixed
    />

    <v-overlay
      :model-value="!isOnline || userId === undefined || !routerReady"
      class="loader-overlay"
      scrim="neutral0"
      :opacity="1"
    >
      <div class="d-flex flex-column align-center justify-center full-height text-neutral900">
        <!-- Cold-launch share intent: show the "adding item" message for the
             whole boot instead of the generic app loader. AddBookmark/AddNote
             mount behind this overlay and their own busy overlay takes over
             seamlessly once auth resolves and this overlay hides. -->
        <IntentAddingMessage v-if="intent && isOnline" />
        <template v-else>
          <v-spacer />
          <v-responsive :aspect-ratio="92/28" max-width="164">
            <Logo class="svg-width text-neutral900" />
          </v-responsive>
          <v-spacer />

          <v-progress-circular v-if="isOnline" color="blue50" indeterminate size="48" />
          <template v-else>
            <span v-text="t('common.NO_NETWORK')" />
            <BrandButton class="mt-3" @click="onNetworkRetry">
              {{ t('cta.try_again') }}
            </BrandButton>
          </template>
          <v-spacer />
        </template>
      </div>
    </v-overlay>

    <UpdateApplicationOverlay />

    <!-- the verification wall: a full account that never verified its email
         sees only this, except on the pages utils/email-verification-wall exempts -->
    <EmailVerificationOverlay v-if="showEmailVerificationWall" />

    <v-overlay
      :model-value="isUnderMaintenance"
      class="loader-overlay"
      scrim="neutral0"
      :opacity="1"
    >
      <div class="d-flex flex-column align-center justify-center full-height text-neutral900">
        <v-spacer />
        <v-responsive :aspect-ratio="92/28" max-width="164">
          <Logo class="svg-width text-neutral900" />
        </v-responsive>
        <h3 class="text-sm-h3 text-h4 text-center gradient-text font-weight-black">
          {{ t('under_maintenance.title') }}
        </h3>
        <p class="release-notification-text text-body-1 text-center mt-3">
          {{ t('under_maintenance.description') }}
        </p>
        <BrandButton
          variant="primary" size="x-large" class="mt-8" :href="updateNewsLink"
          target="_blank"
          rel="noopener noreferrer"
        >
          {{ t('under_maintenance.cta') }}
        </BrandButton>
        <v-spacer />
      </div>
    </v-overlay>

    <ToastFactory />
    <ModalFactory />

    <!-- Every client asks through this dialog. Excluded from the mini popup,
         which renders ExtensionMini instead of a layout and is only 300px
         wide — there is no surface there to put a modal on. -->
    <RatingPromptDialog v-if="ready && !isExtensionMini" />

    <ExtensionMini v-if="ready && isExtensionMini" />
    <Layout v-if="ready && !isExtensionMini" />
  </v-app>
</template>

<script setup>
import { ref, computed, watch, defineAsyncComponent } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useDisplay, useTheme } from 'vuetify'
// import { useHead } from '@vueuse/head'
import { SplashScreen } from '@capacitor/splash-screen'
import { App as CapacitorApp } from '@capacitor/app'

import ToastFactory from '@/components/factories/ToastFactory'
import ModalFactory from '@/components/factories/ModalFactory'
import RatingPromptDialog from '@/components/global/RatingPromptDialog.vue'
import UpdateApplicationOverlay from '@/components/overlays/UpdateApplicationOverlay'
import ExtensionMini from '@/ExtensionMini'
import Layout from '@/components/global/Layout.vue'
import BrandButton from '@/components/global/brand/BrandButton.vue'
import IntentAddingMessage from '@/components/overlays/IntentAddingMessage.vue'
import DisplayWatcher from '@/components/global/DisplayWatcher.vue'
import safeArea from '@/plugins/safe-area-insets'
import toast from '@/plugins/toasts'
import { UPDATE_NEWS_LINKS } from '@/utils/constants'
import { showsEmailVerificationWall } from '@/utils/email-verification-wall'
import { settlePublicCollectionReturn } from '@/utils/public-collection-return'
import { getRouteInheritedMeta } from '@/utils/helpers'
import {
  resolveSystemBarHeight,
  resolveSystemBarColor,
  resolveSystemBarHex,
  ownsSampledStatusStrip,
} from '@/utils/system-bar'
import { useSystemBarColor } from '@/composables/useSystemBarColor'
import { useRatingPrompt } from '@/composables/useRatingPrompt'
import { useHead } from '@unhead/vue'

// only walled users ever load it
const EmailVerificationOverlay = defineAsyncComponent(() => import('@/components/overlays/EmailVerificationOverlay.vue'))

// Set up store, route, and i18n
const store = useStore()
const route = useRoute()
const router = useRouter()
const { t, locale } = useI18n()

// The initial navigation can be blocked in the router guard (e.g. the extension
// floating window awaits auth before resolving /extension/options). Until it
// settles, the current route is the unmatched START_LOCATION, which falls back
// to the 'default' layout (AppBar) — so gate rendering on router readiness to
// avoid flashing that layout before the real route mounts.
const routerReady = ref(false)
router.isReady().finally(() => { routerReady.value = true })
const display = useDisplay()

// Computed properties - store getters
const userId = computed(() => store.getters['auth/userId'])
const isOnline = computed(() => store.getters['device/online'])
const isExtension = computed(() => store.getters['device/isExtension'])
const isExtensionMini = computed(() => store.getters['device/isExtensionMini'])
const isNative = computed(() => store.getters['device/isNative'])
const localeLoaded = computed(() => store.getters['device/localeLoaded'])
const isUnderMaintenance = computed(() => store.getters['features/UnderMaintenance'])
const intent = computed(() => store.getters['native/intent'])

// Other computed properties
const ready = computed(() => localeLoaded.value && isOnline.value && routerReady.value)
// Not in the mini popup: no surface for it there, and its quick add needs no verification.
const showEmailVerificationWall = computed(() => ready.value && !isExtensionMini.value
  && showsEmailVerificationWall(store.getters['auth/session'], route.path))
// The wall lifting in place ("I have verified") hands the page under it back to
// the user: a way back it kept open to that page is consumed.
watch(showEmailVerificationWall, (walled, was) => {
  if (was && !walled) settlePublicCollectionReturn(store, route)
})

// Computed styles to set CSS variables dynamically
const styles = computed(() => {
  // Only apply layout left offset on desktop when sidebar should be visible
  // Use inherited route meta to check sidebar visibility
  const meta = getRouteInheritedMeta(route)
  const showSidebar = display.mdAndUp.value &&
                     (meta.sidebarVisibility === true ||
                      (typeof meta.sidebarVisibility === 'function' &&
                       meta.sidebarVisibility({ display })))

  return {
    '--v-layout-left': showSidebar ? '320px !important' : '0px !important',
  }
})

const ownsStatusStrip = computed(() => ownsSampledStatusStrip({
  isNative: isNative.value,
  isExtension: isExtension.value,
}))

const { systemBarColor } = useSystemBarColor()
const theme = useTheme()

const systemBarProps = computed(() => {
  const parentRoute = route.matched[0]
  const routeSystemBar = {
    ...parentRoute?.meta?.systemBar,
    ...route?.meta?.systemBar,
  }

  return {
    height: resolveSystemBarHeight(safeArea.top, ownsStatusStrip.value),
    ...routeSystemBar,
    // After the route's own meta: a mounted page knows a colour the route cannot
    // (the note's), and every note route hardcodes neutral0.
    color: resolveSystemBarColor(systemBarColor.value, routeSystemBar.color),
  }
})

const updateNewsLink = computed(() => {
  return UPDATE_NEWS_LINKS[locale.value]
})

// Methods
function onNetworkRetry() {
  store.dispatch('device/checkDeviceConnectivity').catch(() => {
    toast.error(t('common.NO_NETWORK'))
  })
}

// In-app rating prompt.
//
// Native: the add screens QUEUE the soft-ask rather than show it in place, and
// it is flushed onto a surface the user can actually see. Three flush points,
// because the surface that queued it is often gone by then: a route change (the
// common case), becoming active again (Android's share Activity kills its own
// WebView the moment MainActivity takes over, so the queued ask is picked up
// here instead), and boot (cold-launched share).
//
// Extension + webapp: no queue — they ask on open, once the app is ready.
const { flushPendingRating, promptOnAppOpen } = useRatingPrompt()
const flushRating = () => { flushPendingRating().catch(() => {}) }

watch(() => route.fullPath, flushRating)
watch(ready, val => {
  if (!val) return
  flushRating()
  promptOnAppOpen().catch(() => {})
}, { immediate: true })
CapacitorApp.addListener('appStateChange', ({ isActive }) => { if (isActive) flushRating() })

// Watchers
watch(ready, async (val) => {
  if (val && store.getters['device/isNative']) {
    await SplashScreen.hide()
  }
}, { immediate: true })

useHead({
  lang: locale,
  title: computed(() => t('page.default.meta.title')),
  titleTemplate: computed(() => (chunk) =>
    t(chunk ? 'page.default.meta.title_template' : 'page.default.meta.title', [chunk])
  ),
  meta: [
    {
      name: 'description',
      content: computed(() => t('page.default.meta.description')),
    },
    {
      name: 'keywords',
      content: computed(() => t('page.default.meta.keywords')),
    },
    {
      // Android Chrome tints its own toolbar from theme-color -- on that client
      // there is no app-drawn strip for the system bar to colour, so this is the
      // equivalent surface. Tracks the same resolved colour the bar uses, which
      // means the note colour on a note page and the app surface everywhere else
      // (the same reason SYSTEM_BAR_DEFAULT_COLOR exists).
      name: 'theme-color',
      content: computed(() => resolveSystemBarHex(systemBarProps.value.color, theme.current.value.colors)),
    },
  ],
})

</script>

<style lang="scss" scoped>
.v-system-bar {
  z-index: 99999;
}

.loader-overlay :deep(.v-overlay__content) {
  height: 100%;
}

.loader-overlay {
  display: flex;
  align-items: center;
  justify-content: center;
}

.gradient-text {
  background:
      linear-gradient(
        to right,
        rgb(var(--v-theme-blue400)),
        rgb(var(--v-theme-teal400))
      );
    background-clip: text;
    color: transparent;
}

.release-notification-text {
  max-width: 500px;
}
</style>
